import { Box, Stack, Typography } from '@mui/material';
import { useCallback } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useAiSuggestionsV2BannerGenerateMutation } from '@/modules/aiSuggestions/components/AISuggestionsV2Banner/AISuggestionsV2Banner.generated';
import { useAISuggestionsV2BannerDismissed } from '@/modules/aiSuggestions/components/AISuggestionsV2Banner/useAISuggestionsV2BannerDismissed';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';

/**
 * Banner announcing the AI Suggestions V2 upgrade. Renders when the
 * household's showGenerateV2SuggestionsPrompt field is true (sourced from
 * the household details context) and the KGv2 AI POC feature flag is on.
 *
 * Callers control where this is rendered (People, Direct Holdings, Trusts,
 * and Businesses tabs per T2-10130). Dismissal via the X button is
 * per-user (localStorage). Generate flips the flag to false server-side,
 * which hides the banner for all users via the Apollo normalized cache.
 */
export function AISuggestionsV2Banner() {
  const { householdId, showGenerateV2SuggestionsPrompt } =
    useHouseholdDetailsContext();
  const { reportError } = useReportError();
  const { showFeedback } = useFeedback();
  const aiKgV2Enabled = useFeatureFlag('aikg_v2_associations_poc');

  const { isDismissed, dismiss } = useAISuggestionsV2BannerDismissed(
    householdId ?? ''
  );

  const [generate, { loading: isGenerating }] =
    useAiSuggestionsV2BannerGenerateMutation({
      onCompleted: () => {
        showFeedback('Suggestions refresh started successfully', {
          variant: 'success',
        });
      },
      onError: (error) => {
        reportError('Failed to refresh household AI suggestions', error);
        showFeedback('Failed to refresh AI suggestions. Please try again.');
      },
    });

  const handleGenerateClick = useCallback(() => {
    if (!householdId) return;
    void generate({
      variables: {
        refreshInput: { householdID: householdId },
        householdID: householdId,
      },
    });
  }, [householdId, generate]);

  if (!householdId || !aiKgV2Enabled || isDismissed) return null;
  if (!showGenerateV2SuggestionsPrompt) return null;

  return (
    <Callout severity="info-high" skipTextWrapper>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        gap={2}
        width="100%"
      >
        <Stack direction="column">
          <Typography variant="title-xs" paddingBottom={0.5}>
            New AI suggestions available
          </Typography>
          <Typography variant="body-xs" paddingBottom={1}>
            Luminary&apos;s AI engine is now more accurate. All outstanding
            suggestions have been reset. Click generate to see the improved
            suggestions across the household.
          </Typography>
          <Box>
            <Button
              variant="primary"
              size="xs"
              loading={isGenerating}
              onClick={handleGenerateClick}
            >
              Generate
            </Button>
          </Box>
        </Stack>
        <IconButton
          variant="transparent"
          size="sm"
          icon={XCloseIcon}
          ariaLabel="Dismiss"
          onClick={dismiss}
        />
      </Stack>
    </Callout>
  );
}
